import React, { useState, useEffect } from 'react';
import {
  Descriptions,
  Spin,
  Empty,
  message,
  Tag,
  Space,
  Typography,
  Tabs,
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  DollarOutlined,
  PhoneFilled,
} from '@ant-design/icons';
import { getLead, Lead } from '../../../../services/lead';
import FollowUpTimeline from '../../../../components/LeadDrawer/FollowUpTimeline';
import FinanceTab from '../../../../components/LeadDrawer/FinanceTab';
import CallRecordsTab from '../../../../components/LeadDrawer/CallRecordsTab';
import AttachmentUpload from '../../../../components/AttachmentUpload';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TabPane } = Tabs;

interface CaseLeadTabProps {
  leadId: number | null;
}

const CaseLeadTab: React.FC<CaseLeadTabProps> = ({ leadId }) => {
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState<Lead | null>(null);
  const [activeSubTab, setActiveSubTab] = useState('info');

  useEffect(() => {
    const loadLead = async () => {
      if (!leadId) return;

      setLoading(true);
      try {
        const data = await getLead(leadId);
        setLead(data);
      } catch (error) {
        message.error('加载线索信息失败');
      } finally {
        setLoading(false);
      }
    };

    loadLead();
  }, [leadId]);

  if (!leadId) {
    return (
      <Empty
        description="该案件未关联线索"
        style={{ padding: '60px 0' }}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!lead) {
    return (
      <Empty
        description="线索信息不存在"
        style={{ padding: '60px 0' }}
      />
    );
  }

  // 构建地区字符串
  const regionText = [lead.province, lead.city, lead.district]
    .filter(Boolean)
    .join('');

  // 渲染基本信息Tab
  const renderInfoTab = () => (
    <div style={{ padding: '24px' }}>
      <Descriptions
        title="线索基本信息"
        bordered
        column={2}
        size="middle"
      >
        <Descriptions.Item
          label={
            <Space size={4}>
              <UserOutlined />
              <span>联系人</span>
            </Space>
          }
        >
          {lead.contact_name || '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space size={4}>
              <PhoneOutlined />
              <span>手机号</span>
            </Space>
          }
        >
          {lead.phone || '-'}
        </Descriptions.Item>

        <Descriptions.Item label="线索类型">
          {lead.lead_type || '-'}
        </Descriptions.Item>

        <Descriptions.Item label="紧急程度">
          {lead.urgency_level ? (
            <Tag color={
              lead.urgency_level === 'A类' ? 'red' :
              lead.urgency_level === 'B类' ? 'orange' :
              lead.urgency_level === 'C类' ? 'blue' : 'default'
            }>
              {lead.urgency_level}
            </Tag>
          ) : '-'}
        </Descriptions.Item>

        <Descriptions.Item label="跟进意向">
          {lead.follow_status ? (
            <Tag color={
              lead.follow_status === '已成交' ? 'success' :
              lead.follow_status === '已付咨询费' ? 'green' :
              lead.follow_status === '邀约到所' ? 'purple' :
              lead.follow_status === '已加企微' ? 'cyan' :
              lead.follow_status === '已加微信' ? 'blue' :
              lead.follow_status === '不需要' || lead.follow_status === '无效' ? 'error' :
              'default'
            }>
              {lead.follow_status}
            </Tag>
          ) : '-'}
        </Descriptions.Item>

        <Descriptions.Item label="负责人">
          {lead.assignee_name || '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space size={4}>
              <EnvironmentOutlined />
              <span>所在地区</span>
            </Space>
          }
          span={2}
        >
          {regionText || '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space size={4}>
              <CalendarOutlined />
              <span>到访时间</span>
            </Space>
          }
        >
          {lead.visit_time ? dayjs(lead.visit_time).format('YYYY-MM-DD HH:mm') : '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space size={4}>
              <CalendarOutlined />
              <span>下次跟进时间</span>
            </Space>
          }
        >
          {lead.next_follow_time ? dayjs(lead.next_follow_time).format('YYYY-MM-DD HH:mm') : '-'}
        </Descriptions.Item>

        <Descriptions.Item label="案件描述" span={2}>
          <Text style={{ whiteSpace: 'pre-wrap' }}>
            {lead.case_description || '-'}
          </Text>
        </Descriptions.Item>

        <Descriptions.Item label="创建时间">
          {lead.created_at ? dayjs(lead.created_at).format('YYYY-MM-DD HH:mm') : '-'}
        </Descriptions.Item>

        <Descriptions.Item label="创建人">
          {lead.creator_name || '-'}
        </Descriptions.Item>
      </Descriptions>

    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Tabs
        activeKey={activeSubTab}
        onChange={setActiveSubTab}
        tabPosition="left"
        className="case-lead-tabs"
      >
        <TabPane
          tab={
            <Space>
              <UserOutlined />
              <span>基本信息</span>
            </Space>
          }
          key="info"
        >
          {renderInfoTab()}
        </TabPane>

        <TabPane
          tab={
            <Space>
              <FileTextOutlined />
              <span>跟进记录</span>
            </Space>
          }
          key="followup"
        >
          <div 
            className="case-lead-followup-container"
            style={{ padding: '16px 24px', overflowY: 'auto' }}
          >
            <FollowUpTimeline leadId={leadId!} refreshTrigger={0} />
          </div>
        </TabPane>

        <TabPane
          tab={
            <Space>
              <PaperClipOutlined />
              <span>附件管理</span>
            </Space>
          }
          key="attachment"
        >
          <div style={{ padding: '24px', height: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <AttachmentUpload
              entityType="lead"
              entityId={leadId!}
              maxCount={10}
              disabled={true}
            />
          </div>
        </TabPane>

        <TabPane
          tab={
            <Space>
              <DollarOutlined />
              <span>财务记录</span>
            </Space>
          }
          key="finance"
        >
          <div 
            className="case-lead-finance-container"
            style={{ padding: '24px', minHeight: '700px', overflowY: 'auto' }}
          >
            {lead && <FinanceTab leadId={leadId!} lead={lead} canEdit={false} />}
          </div>
        </TabPane>

        <TabPane
          tab={
            <Space>
              <PhoneFilled />
              <span>外呼记录</span>
            </Space>
          }
          key="call"
        >
          <div style={{ padding: '24px', height: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <CallRecordsTab leadId={leadId!} />
          </div>
        </TabPane>
      </Tabs>

      <style>{`
        .case-lead-tabs .ant-tabs-nav {
          width: 98px !important;
          min-width: 95px !important;
          padding-left: 0px !important;
        }
        .case-lead-tabs .ant-tabs-content-holder {
          margin-left: 0 !important;
        }
        .case-lead-tabs .ant-tabs-tab {
          padding: 10px 2px 10px 0px !important;
          justify-content: flex-start !important;
        }
        .case-lead-tabs .ant-tabs-tab .ant-space {
          gap: 4px !important;
        }
        .case-lead-tabs .ant-tabs-tab .ant-space span {
          font-size: 13px !important;
        }
        /* 只针对跟进记录Tab中的FollowUpTimeline组件 */
        .case-lead-followup-container .follow-up-timeline {
          max-height: 600px !important;
        }
      `}</style>
    </div>
  );
};

export default CaseLeadTab;
